import { NotificationEnum } from "@graphql/generated/graphql.g";
import {
  NotificationFragment,
  useNotificationsQuery,
} from "@graphql/notifications/Notifications.g";
import {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { Box } from "@design-system/Box/Box";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";
import { ConfigurableTableSystem } from "@utils/table/TableConfigurationSystem";

import { ClientApplicationReviewPanel } from "./ClientApplicationReviewPanel/ClientApplicationReviewPanel";

import {
  ClientNotificationsHistoryTableConfig,
  ClientNotificationsTableConfig,
  renderApplicationName,
  renderCreatedAt,
  renderEmployee,
  renderNotificationType,
  renderReason,
  renderResolvedAt,
  renderResolvedBy,
} from "./ClientNotificationsTableConfig";
import { ClientNotificationTableColumns } from "./UI/ClientNotificationsTableColumns";
import type { NotificationsViewMode } from "../NotificationsTable/NotificationsTable";

const PAGE_SIZE = 30;

const columnHelper = createColumnHelper<NotificationFragment>();

type Props = {
  initialPageIndex?: number;
  clientId: string;
  viewMode?: NotificationsViewMode;
  notificationType?: NotificationEnum[];
  applicationNameSearch?: string;
  resolvedById?: string;
  resolvedAt_Gte?: string;
  resolvedAt_Lte?: string;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
};

export const ClientNotificationsTable = ({
  initialPageIndex = 0,
  clientId,
  viewMode = "active",
  notificationType,
  applicationNameSearch,
  resolvedById,
  resolvedAt_Gte,
  resolvedAt_Lte,
  sorting: controlledSorting,
  onSortingChange,
}: Props) => {
  const isHistoryView = viewMode === "history";
  const tableConfig = isHistoryView
    ? ClientNotificationsHistoryTableConfig
    : ClientNotificationsTableConfig;

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  const [internalSorting, setInternalSorting] = useState<SortingState>(() =>
    isHistoryView
      ? [{ id: ClientNotificationTableColumns.resolvedAt, desc: true }]
      : [],
  );
  const sorting = controlledSorting ?? internalSorting;
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;
      if (onSortingChange) {
        onSortingChange(newSorting);
      } else {
        setInternalSorting(newSorting);
      }
    },
    [sorting, onSortingChange],
  );
  const [selectedNotificationForReview, setSelectedNotificationForReview] =
    useState<NotificationFragment | null>(null);

  const { columnSettings, columnOrder } = useTableColumnSettingsProvider();

  const effectiveColumnSettings = useMemo(() => {
    if (!isHistoryView) {
      return {
        ...columnSettings,
        [ClientNotificationTableColumns.resolvedBy]: false,
        [ClientNotificationTableColumns.resolvedAt]: false,
      };
    }

    return columnSettings;
  }, [columnSettings, isHistoryView]);

  useEffect(() => {
    setPagination((current) => ({
      pageIndex: 0,
      pageSize: current.pageSize,
    }));
  }, [
    applicationNameSearch,
    notificationType,
    resolvedById,
    resolvedAt_Gte,
    resolvedAt_Lte,
    isHistoryView,
  ]);

  const queryVariables = useMemo(() => {
    const variables: Parameters<typeof useNotificationsQuery>[0] = {
      clientId,
      first: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      resolvedAt_Isnull: !isHistoryView,
      sortBy: ConfigurableTableSystem.generateSortParameter(
        tableConfig.columns,
        sorting,
      ),
      notificationType_In:
        notificationType && notificationType.length > 0
          ? notificationType
          : undefined,
      application_Resource_ProductName_Icontains:
        applicationNameSearch && applicationNameSearch.trim()
          ? applicationNameSearch.trim()
          : undefined,
    };

    if (isHistoryView) {
      if (resolvedById) {
        variables.resolvedById = resolvedById;
      }

      if (resolvedAt_Gte) {
        variables.resolvedAt_Gte = resolvedAt_Gte;
      }

      if (resolvedAt_Lte) {
        variables.resolvedAt_Lte = resolvedAt_Lte;
      }
    }

    return variables;
  }, [
    clientId,
    pagination.pageSize,
    pagination.pageIndex,
    isHistoryView,
    tableConfig.columns,
    sorting,
    notificationType,
    applicationNameSearch,
    resolvedById,
    resolvedAt_Gte,
    resolvedAt_Lte,
  ]);

  const notificationsQuery = useNotificationsQuery(queryVariables);

  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const totalCount =
    notificationsQuery.data?.notifications?.totalCount ?? undefined;

  const data = useMemo(() => {
    return (
      notificationsQuery.data?.notifications?.edges
        .map((edge) => edge?.node)
        .filter((notification) => !!notification) ?? []
    );
  }, [notificationsQuery.data]);

  const handleDetailsClick = useCallback(
    (clientId: string, applicationId: string) => {
      navigate(
        routes.portal.clients.client.applications.application.$buildPath({
          params: { clientId, applicationId },
        }),
        {
          viewTransition: true,
        },
      );
    },
    [navigate],
  );

  const handleNotificationClick = useCallback(
    (notificationId: string) => {
      setSearchParams((prev) => {
        prev.set("notificationId", notificationId);
        return prev;
      });
    },
    [setSearchParams],
  );

  const handleReviewClick = useCallback((notification: NotificationFragment) => {
    setSelectedNotificationForReview(notification);
  }, []);

  const handleCloseReviewPanel = useCallback(() => {
    setSelectedNotificationForReview(null);
  }, []);

  const handleReviewSuccess = useCallback(() => {
    notificationsQuery.refetch();
  }, [notificationsQuery]);

  const configuredColumns = useMemo(() => {
    const configWithRenderers = {
      ...tableConfig,
      columns: Object.fromEntries(
        Object.entries(tableConfig.columns).map(([key, config]) => [
          key,
          {
            ...config,
            render: (value: unknown, row: NotificationFragment) => {
              const columnKey = key as ClientNotificationTableColumns;

              switch (columnKey) {
                case ClientNotificationTableColumns.applicationName:
                  return renderApplicationName(value, row, handleDetailsClick);
                case ClientNotificationTableColumns.employee:
                  return renderEmployee(value, row);
                case ClientNotificationTableColumns.reason:
                  return renderReason(value, row);
                case ClientNotificationTableColumns.notificationType:
                  return renderNotificationType(
                    value,
                    row,
                    handleNotificationClick,
                  );
                case ClientNotificationTableColumns.createdAt:
                  return renderCreatedAt(value);
                case ClientNotificationTableColumns.resolvedBy:
                  return renderResolvedBy(value, row);
                case ClientNotificationTableColumns.resolvedAt:
                  return renderResolvedAt(value);
                default:
                  return String(value);
              }
            },
          },
        ]),
      ) as typeof tableConfig.columns,
    };

    return ConfigurableTableSystem.generateColumnsFromConfig(
      data,
      configWithRenderers,
      {
        columnOrder:
          columnOrder.length > 0
            ? (columnOrder as ClientNotificationTableColumns[])
            : undefined,
        columnVisibility: effectiveColumnSettings,
      },
    );
  }, [
    data,
    columnOrder,
    effectiveColumnSettings,
    handleDetailsClick,
    handleNotificationClick,
    tableConfig,
  ]);

  const allColumnsWithActions = useMemo(() => {
    const columns: ColumnDef<NotificationFragment, unknown>[] = [
      ...configuredColumns,
    ];

    columns.push(
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          sticky: true,
          actions: true,
        },
        cell: (props) => {
          const notification = props.row.original;
          const isSaasDiscovered =
            notification.notificationType === NotificationEnum.SaasDiscovered;

          return (
            <Box gap={2}>
              <Dropdown
                trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
              >
                <Dropdown.Option
                  label="Details"
                  icon="faFileLines"
                  onSelect={() => handleNotificationClick(notification.id)}
                />
                {!isHistoryView && isSaasDiscovered && (
                  <Dropdown.Option
                    label="Review"
                    icon="faPencil"
                    onSelect={() => handleReviewClick(notification)}
                  />
                )}
              </Dropdown>
            </Box>
          );
        },
      }),
    );

    return columns;
  }, [
    configuredColumns,
    handleNotificationClick,
    handleReviewClick,
    isHistoryView,
  ]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <>
      <Table<NotificationFragment>
        columns={allColumnsWithActions as ColumnDef<NotificationFragment>[]}
        data={data}
        rowCount={totalCount}
        onPaginationChange={handlePaginationChange}
        pagination={pagination}
        isLoading={notificationsQuery.isLoading}
        isFetching={notificationsQuery.isFetching}
        onPageSizeChange={handlePageSizeChange}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        columnVisibility={effectiveColumnSettings}
      />

      <ClientApplicationReviewPanel
        notification={selectedNotificationForReview}
        isOpen={!!selectedNotificationForReview}
        onClose={handleCloseReviewPanel}
        onSuccess={handleReviewSuccess}
      />
    </>
  );
};
